import { NavLink, useLocation } from "react-router-dom";
import * as Icons from "lucide-react";
import { NAV_GROUPS } from "./navConfig";
import { cn } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import { useNotifications } from "@/context/NotificationsContext";
import { isFirebaseConfigured, firebaseProjectId } from "@/lib/firebase";

const FIELD_NAV = [
  { key: "field", label: "Pro Onboarding", icon: "UserPlus", path: "/field/new-professional" },
];

export function Sidebar({ open, onClose }) {
  const location = useLocation();
  const { user } = useAuth();
  const groups = user?.role === "field" ? [{ label: null, items: FIELD_NAV }] : NAV_GROUPS;
  const { unseenBySection } = useNotifications();
  return (
    <>
      {open && (
        <div className="fixed inset-0 z-30 bg-black/40 lg:hidden" onClick={onClose} data-testid="sidebar-overlay" />
      )}
      <aside
        data-testid="app-sidebar"
        className={cn(
          "fixed z-40 top-0 left-0 h-full w-64 border-r border-border bg-card flex flex-col transition-transform duration-300 lg:translate-x-0",
          open ? "translate-x-0" : "-translate-x-full"
        )}
      >
        <div className="h-20 flex items-center gap-3 px-5 border-b border-border shrink-0">
          <img src="/brugo-logo.png" alt="Brugo" className="h-9 w-auto" data-testid="sidebar-logo" />
          <div className="leading-tight border-l border-border pl-3">
            <p className="text-[11px] font-heading font-bold uppercase tracking-wider text-foreground">Command</p>
            <p className="text-[11px] font-heading font-bold uppercase tracking-wider text-primary">Center</p>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto px-3 py-3">
          {groups.map((g) => (
            <div key={g.label || "main"} className="mb-3 last:mb-0">
              {g.label && <p className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground/70">{g.label}</p>}
              <div className="space-y-0.5">
                {g.items.map((item) => {
                  const Icon = Icons[item.icon] || Icons.Circle;
                  const active = item.path === "/" ? location.pathname === "/" : location.pathname.startsWith(item.path);
                  const n = unseenBySection[item.key] || 0;
                  return (
                    <NavLink
                      key={item.key}
                      to={item.path}
                      onClick={onClose}
                      data-testid={`nav-${item.key}`}
                      className={cn(
                        "group relative flex items-center gap-3 rounded-lg px-3 py-2 text-[13px] font-medium transition-colors",
                        active
                          ? "bg-primary/10 text-primary before:absolute before:left-0 before:top-1.5 before:bottom-1.5 before:w-[3px] before:rounded-full before:bg-primary"
                          : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                      )}
                    >
                      <Icon className="h-[17px] w-[17px] shrink-0" />
                      <span className="truncate flex-1">{item.label}</span>
                      {n > 0 && (item.key === "notifications"
                        ? <span data-testid={`nav-count-${item.key}`} className="min-w-5 h-5 px-1.5 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center">{n > 99 ? "99+" : n}</span>
                        : <span data-testid={`nav-dot-${item.key}`} title={`${n} new`} className="cc-new-dot" />)}
                    </NavLink>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        <div className="border-t border-border p-3 shrink-0">
          <div className="flex items-center gap-2 rounded-lg bg-secondary px-3 py-2" data-testid="firebase-status">
            <span className={cn("h-2 w-2 rounded-full", isFirebaseConfigured ? "bg-emerald-500" : "bg-amber-500")} />
            <div className="leading-tight overflow-hidden">
              <p className="text-[11px] font-medium text-foreground truncate">
                {isFirebaseConfigured ? "Firebase Connected" : "Not configured"}
              </p>
              <p className="text-[10px] font-mono text-muted-foreground truncate">{firebaseProjectId}</p>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
}
